import React from "react";
import CloseIcon from "@src/assets/svg/close-icon.svg";
import { Button } from "@src/components/atoms/buttons/button/button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import Spinner from "@src/components/atoms/spinner/spinner";
import clearWithCircleBlueIcon from "@src/assets/svg/close-with-circle-blue-icon.svg";
import styles from "@src/components/organisms/modals/add-machine-groups-admin-panel-modal/add-machine-groups-admin-panel-modal.module.scss";
import {
  CrossNameSpaceOrganziation,
  MachineGroup,
} from "@src/types/namespace-configuration";
import { useAddMachineGroupsAdminPanelModalHook } from "@src/components/organisms/modals/add-machine-groups-admin-panel-modal/use-add-machine-groups-admin-panel-modal-hook";
import { AdminPanelOrganizationDropdown } from "@src/components/atoms/admin-panel-organization-dropdown/admin-panel-organization-dropdown";

const cssClassName = "add-machine-groups-admin-panel-modal";

interface AddMachineGroupsAdminPanelModalProps {
  editingMachineGroup?: MachineGroup;
  isLoadingOrganizations: boolean;
  onMachineGroupSaved: (createdMachineGroupId?: string) => void;
  organizations: CrossNameSpaceOrganziation[];
}

export const AddMachineGroupsAdminPanelModal = ({
  editingMachineGroup,
  isLoadingOrganizations,
  onMachineGroupSaved,
  organizations,
}: AddMachineGroupsAdminPanelModalProps) => {
  const {
    closeModal,
    formValues,
    handleGroupNameChange,
    handleClearGroupName,
    handleOrganizationChange,
    handleSave,
    isLoadingMachines,
    isSaving,
    machines,
    organizationOptions,
    organizationPlaceholder,
    texts,
    toggleMachine,
    toggleAllMachines,
    checkBoxIcon,
    checkboxIndeterminateIcon,
    checkedCheckboxIcon,
  } = useAddMachineGroupsAdminPanelModalHook(
    editingMachineGroup,
    onMachineGroupSaved,
    organizations,
    isLoadingOrganizations,
  );

  const selectedMachineCount = machines.filter((machine) =>
    formValues.source_ids.includes(machine.sourceId),
  ).length;
  const allMachinesSelected =
    machines.length > 0 && selectedMachineCount === machines.length;
  const someMachinesSelected = selectedMachineCount > 0 && !allMachinesSelected;

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__overlay`]} />
      <div
        className={styles[`${cssClassName}__dialog`]}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${cssClassName}-title`}
      >
        <header className={styles[`${cssClassName}__header`]}>
          <h2 id={`${cssClassName}-title`}>{texts.title}</h2>
          <div className={styles[`${cssClassName}__close-button`]}>
            <IconButton fun={closeModal} icon={CloseIcon} />
          </div>
        </header>
        <div className={styles[`${cssClassName}__divider`]} />
        <div className={styles[`${cssClassName}__fields`]}>
          <div className={styles[`${cssClassName}__group-name-field`]}>
            <label
              className={styles[`${cssClassName}__group-name-label`]}
              htmlFor={`${cssClassName}-group-name`}
            >
              {texts.groupNameLabel}
            </label>
            <div
              className={styles[`${cssClassName}__group-name-input-wrapper`]}
            >
              <input
                className={styles[`${cssClassName}__group-name-input`]}
                id={`${cssClassName}-group-name`}
                maxLength={255}
                onChange={handleGroupNameChange}
                placeholder={texts.groupNamePlaceholder}
                type="text"
                value={formValues.name}
              />
              <button
                aria-label="Clear group name"
                className={styles[`${cssClassName}__group-name-clear`]}
                disabled={!formValues.name}
                onClick={handleClearGroupName}
                type="button"
              >
                <img alt="" aria-hidden="true" src={clearWithCircleBlueIcon} />
              </button>
            </div>
          </div>
          <AdminPanelOrganizationDropdown
            isLoading={isLoadingOrganizations}
            label={texts.organizationLabel}
            onChange={handleOrganizationChange}
            options={organizationOptions}
            placeholder={organizationPlaceholder}
            selectedId={formValues.organization_id}
          />
        </div>
        <section className={styles[`${cssClassName}__machine-section`]}>
          <div className={styles[`${cssClassName}__section-label`]}>
            {texts.machineLibraryLabel}
          </div>
          <div
            aria-busy={isLoadingMachines}
            className={styles[`${cssClassName}__machine-library`]}
          >
            {!formValues.organization_id ? (
              <div className={styles[`${cssClassName}__machine-library-empty`]}>
                {texts.chooseOrganizationToSeeMachineLibrary}
              </div>
            ) : isLoadingMachines ? (
              <Spinner />
            ) : machines.length === 0 ? (
              <div className={styles[`${cssClassName}__machine-library-empty`]}>
                {texts.noMachinesFound}
              </div>
            ) : (
              <table className={styles[`${cssClassName}__machine-table`]}>
                <thead>
                  <tr>
                    <th scope="col">
                      <label
                        className={styles[`${cssClassName}__checkbox-cell`]}
                      >
                        <input
                          aria-checked={
                            someMachinesSelected ? "mixed" : allMachinesSelected
                          }
                          aria-label="Select all machines"
                          checked={allMachinesSelected}
                          className={styles[`${cssClassName}__checkbox-input`]}
                          disabled={machines.length === 0}
                          onChange={toggleAllMachines}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = someMachinesSelected;
                            }
                          }}
                          type="checkbox"
                        />
                        <img
                          alt=""
                          aria-hidden="true"
                          className={styles[`${cssClassName}__checkbox-icon`]}
                          src={
                            allMachinesSelected
                              ? checkedCheckboxIcon
                              : someMachinesSelected
                              ? checkboxIndeterminateIcon
                              : checkBoxIcon
                          }
                        />
                      </label>
                    </th>
                    <th scope="col">{texts.machineName}</th>
                    <th scope="col">{texts.manufacturer}</th>
                    <th scope="col">{texts.ipAddress}</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.map((machine) => {
                    const isSelected = formValues.source_ids.includes(
                      machine.sourceId,
                    );

                    return (
                      <tr key={machine.sourceId}>
                        <td>
                          <label
                            className={styles[`${cssClassName}__checkbox-cell`]}
                          >
                            <input
                              aria-label={`Select ${machine.name}`}
                              checked={isSelected}
                              className={
                                styles[`${cssClassName}__checkbox-input`]
                              }
                              onChange={() => toggleMachine(machine.sourceId)}
                              type="checkbox"
                            />
                            <img
                              alt=""
                              aria-hidden="true"
                              className={
                                styles[`${cssClassName}__checkbox-icon`]
                              }
                              src={
                                isSelected ? checkedCheckboxIcon : checkBoxIcon
                              }
                            />
                          </label>
                        </td>
                        <td>{machine.name}</td>
                        <td>{machine.manufacturer}</td>
                        <td>{machine.hostname}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
        <footer className={styles[`${cssClassName}__footer`]}>
          <div className={styles[`${cssClassName}__divider`]} />
          <div className={styles[`${cssClassName}__actions`]}>
            <Button
              type="button"
              text={texts.saveButton}
              variant="primary"
              disabled={isSaving}
              fun={handleSave}
            />
            <Button
              type="button"
              text={texts.cancelButton}
              variant="secondary"
              fun={closeModal}
            />
          </div>
        </footer>
      </div>
    </div>
  );
};
